import React, { useEffect } from "react";
import {
  BlockHeroCompact,
  BlockSection,
  BlockText,
  BlockFigure,
  ImageSequence,
  RecipeRow,
  RecipeCell,
  RecipeBoard,
  projectPageShellClass,
  projectPageBottomClass,
  blockSectionBodyWrapClass,
} from "../../designSystem/projectBlocks";

const BASE = "/projects/ECHO/images";

export default function EchoPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formingFrames = [
    `${BASE}/ECHO Forming 1.png`,
    `${BASE}/ECHO Forming 2.png`,
    `${BASE}/ECHO Forming 3.png`,
    `${BASE}/ECHO Forming 4.png`,
    `${BASE}/ECHO Forming 5.png`,
  ];
  const formingCaps = ["Step 01", "Step 02", "Step 03", "Step 04", "Step 05"];

  return (
    <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
      <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
        <BlockHeroCompact
          title="ECHO"
          description="A marine rehabilitation, research, and aquarium complex on the northern shore of Sinop, Turkey. The form echoes the town’s historical castle ruins; the project mission echoes the call to protect the Black Sea’s endangered species."
          meta={["ARCH401 · Architectural Design Studio", "Sinop, Turkey", "Rhino", "Grasshopper", "V-Ray", "2024"]}
        />

        <RecipeBoard>
          <RecipeRow>
            <RecipeCell span={12} className="!p-0 !border-r-0">
              <BlockFigure
                src={`${BASE}/Card_r12.png`}
                alt="ECHO — primary architectural render"
                ratio="banner"
                fit="cover"
              />
            </RecipeCell>
          </RecipeRow>
        </RecipeBoard>

        <BlockSection title="Design orientation">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "ECHO is positioned beside the remains of Sinop’s historical castle wall and along the northern wave-breaker. The site links two evident aspects of the town — the castle ruins and the Black Sea — and lets the building speak to both.",
              "Turkey hosts 163 endangered marine species, including 62 species of fish and 11 amphibians. The brief responds with three programmes: an educational institute for sustainable marine ecology, a rehabilitation centre for endangered animals, and a public aquarium that funds the work and raises awareness.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={8} className="!p-0">
                <BlockFigure
                  src={`${BASE}/Arch System-logo.png`}
                  alt="ECHO — architectural system overview"
                  ratio="classic"
                  fit="contain"
                  caption="Architectural system overview"
                />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0 !border-r-0">
                <ImageSequence
                  images={formingFrames}
                  captions={formingCaps}
                  label="Forming sequence"
                  ratio="classic"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Geometric algorithms">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "The collapsing-castle form is resolved through three parametric algorithms working in series. The Planarity algorithm keeps every surface planar while randomising panel rotations to suggest collapse. The Eater algorithm detects intersections between adjacent masses and subtracts the smaller from the larger where they overlap. The Envelope algorithm reads the resulting envelope for openings and instantiates windows, skylights, and fenestration in the appropriate panels.",
              "Solar-radiation analysis tunes the geometry for daylight and shading — adjusting massing to balance Useful Daylight (UDL) hours against solar gain.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/1st diag Pothoshoped.png`} alt="Planarity algorithm diagram" ratio="square" fit="contain" caption="Planarity" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/2nd diag Photoshoped.png`} alt="Eater algorithm diagram" ratio="square" fit="contain" caption="Eater" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/3rd diag photoshoped.png`} alt="Envelope algorithm diagram" ratio="square" fit="contain" caption="Envelope" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Environmental analysis">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Site simulations measured incident radiation, direct sun hours, wind speed, and dry-bulb temperature across the year — feeding each layer back into the geometric algorithms to refine the envelope.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/IncidentRadiation.png`} alt="Incident radiation analysis" ratio="classic" fit="contain" caption="Incident radiation" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/DirectSunHours.png`} alt="Direct sun hours analysis" ratio="classic" fit="contain" caption="Direct sun hours" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/WindSpeed.png`} alt="Wind speed analysis" ratio="classic" fit="contain" caption="Wind speed" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/DryBulbTemp.png`} alt="Dry-bulb temperature analysis" ratio="classic" fit="contain" caption="Dry-bulb temperature" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Renders and visualisations">
          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/r12.png`} alt="ECHO — render 1" ratio="classic" fit="cover" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/r31light.png`} alt="ECHO — render 2" ratio="classic" fit="cover" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/ri2.png`} alt="ECHO — interior render" ratio="classic" fit="cover" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/MullionDetailing.png`} alt="Mullion detailing" ratio="classic" fit="contain" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>
      </div>
    </main>
  );
}
